import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { isCancelled } from '@seek/ca-http-client';
import type { UrlLocation } from '@seek/chalice-types';
import { parse as parseCookies } from 'cookie';
import mapKeys from 'lodash/fp/mapKeys';
import get from 'lodash/get';
import type { RouteObject } from 'react-router';
// @ts-expect-error: non-ts file
import { trigger } from 'redial';

import getCountryFromZone from 'src/config/utils/getCountryFromZone';
import type { AnalyticsFacade } from 'src/modules/AnalyticsFacade';
import { removeUserCookie, userCookieExists } from 'src/modules/client-context';
import {
  isGraphQLRateLimitedError,
  isWAFForbiddenError,
} from 'src/modules/graphql';
import { logger } from 'src/modules/logger';
import {
  endTransaction,
  startTransaction,
} from 'src/modules/redux-batched-subscribe-transaction';
import { getTransaction } from 'src/store/transactions';
import type { TypedDispatch, TypedGetState } from 'src/store/types';
import type { RedialLocals } from 'src/types/RedialLocals';

// Used to calculate 'routeEnter' values for each hook:
let prevRouteHandlers: Array<RouteObject['Component']> = [];

export const fetchDataForRoutes = async ({
  analyticsFacade,
  matchedRoutes,
  location,
  dispatch,
  getState,
  apolloClient,
  waitForAuth,
  visitorId,
}: {
  analyticsFacade: AnalyticsFacade;
  matchedRoutes?: RouteObject[];
  location: UrlLocation;
  dispatch: TypedDispatch;
  getState: TypedGetState;
  apolloClient: ApolloClient<NormalizedCacheObject>;
  waitForAuth: () => Promise<boolean>;
  visitorId: string;
}) => {
  /**
   * This function needs to be invoked to sync the authentication status in the store on the client side ..
   * .. which is used in many places to do authentication checks.
   *
   * Be cautious when changing/removing this invocation, as it can have unintended side effects.
   */
  const authenticated = await waitForAuth();

  // Keep a local copy of the previous route handlers array.
  // This is so the 'prevRouteHandlers' array can be safely updated mid-lifecycle.
  const prevRouteHandlersCopy = [...prevRouteHandlers];

  const lowercasedQuery = mapKeys((k) => k.toLowerCase(), location.query);

  // Restore data from state
  const {
    appConfig: { brand, language: languageCode, zone, locale },
  } = getState();
  const country = getCountryFromZone(zone);

  const getLocals = (routeHandler: RouteObject['Component']): RedialLocals => ({
    analyticsFacade,
    apolloClient,
    authenticated,
    brand,
    cookies: parseCookies(window.document.cookie),
    country,
    dispatch,
    getState,
    languageCode,
    locale,
    path: location.pathname,
    query: lowercasedQuery,
    referrer: window.document.referrer,
    routeEnter: prevRouteHandlersCopy.indexOf(routeHandler) === -1,
    userAgent: window.navigator.userAgent,
    visitorId,
    zone,
  });

  const routeHandlers = matchedRoutes?.map((route) =>
    get(route, 'Component', undefined),
  );

  prevRouteHandlers = routeHandlers || [];

  const makeTrigger = (name: string) => () =>
    trigger(name, routeHandlers, getLocals);

  let triggerFetch = () => Promise.resolve();
  let triggerSeo = () => Promise.resolve();

  if (window.SEEK_REDUX_DATA) {
    delete window.SEEK_REDUX_DATA;
  } else {
    triggerFetch = makeTrigger('fetch');
    triggerSeo = makeTrigger('seo');
  }

  const triggerFirst = makeTrigger('first');
  const triggerDefer = makeTrigger('defer');
  const triggerPageLoad = makeTrigger('pageLoad');
  const triggerServerFlow = () => triggerFetch().then(triggerSeo);

  const routeChangeTransaction = getTransaction('routeChange');
  // @ts-ignore
  dispatch(startTransaction(routeChangeTransaction));

  return Promise.all([triggerFirst(), triggerServerFlow(), triggerDefer()])
    .catch((err) => {
      if (isWAFForbiddenError(err)) {
        return logger.error(err, 'Error: Forbidden.');
      }
      if (isGraphQLRateLimitedError(err)) {
        return logger.error(err, 'Error: Too many requests.');
      }
      if (isCancelled(err)) {
        logger.warn(err, 'Cancelled AuthFlow');
      }
      logger.warn(err, 'Error AuthFlow');
      throw err;
    })
    .then(triggerPageLoad)
    .then(() => {
      if (!authenticated && userCookieExists()) {
        removeUserCookie();
      }
    })
    .then(() =>
      // @ts-ignore
      dispatch(endTransaction(routeChangeTransaction)),
    );
};
